import { DateFieldShell } from '@components/DateField/DateFieldShell';
import {
  DEFAULT_DATE_PLACEHOLDER,
  formatDate,
} from '@components/DateField/dateField.utils';
import { useAppTranslation } from '@hooks/useAppTranslation';
import { useMemo, useState } from 'react';
import { DatePickerModal } from 'react-native-paper-dates';

export interface DateRangeValue {
  startDate: Date | null;
  endDate: Date | null;
}

export interface DateRangeFieldProps {
  label?: string;
  startDate: Date | null;
  endDate: Date | null;
  onValueChange: (value: DateRangeValue) => void;
  placeholder?: string;
  required?: boolean;
}

export const DateRangeField = ({
  label,
  startDate,
  endDate,
  onValueChange,
  placeholder = DEFAULT_DATE_PLACEHOLDER,
  required = false,
}: DateRangeFieldProps): React.ReactElement => {
  const { language, t } = useAppTranslation();
  const [isPickerVisible, setIsPickerVisible] = useState(false);

  const formattedRange = useMemo(() => {
    if (startDate && endDate) {
      return `${formatDate(startDate, language)} - ${formatDate(endDate, language)}`;
    }

    if (startDate) {
      return `${formatDate(startDate, language)} - ${t('filters.dateRange.today')}`;
    }

    return '';
  }, [endDate, language, startDate, t]);

  const handleOpenPicker = (): void => {
    setIsPickerVisible(true);
  };

  const handleDismiss = (): void => {
    setIsPickerVisible(false);
  };

  const handleChange = ({
    startDate: nextStart,
    endDate: nextEnd,
  }: {
    startDate: Date | undefined;
    endDate: Date | undefined;
  }): void => {
    onValueChange({
      startDate: nextStart ?? null,
      endDate: nextEnd ?? null,
    });
  };

  const handleConfirm = ({
    startDate: nextStart,
    endDate: nextEnd,
  }: {
    startDate: Date | undefined;
    endDate: Date | undefined;
  }): void => {
    setIsPickerVisible(false);
    onValueChange({
      startDate: nextStart ?? null,
      endDate: nextEnd ?? null,
    });
  };

  const handleDelete = (): void => {
    onValueChange({
      startDate: null,
      endDate: null,
    });
  };

  return (
    <>
      <DateFieldShell
        label={label}
        required={required}
        valueText={formattedRange}
        placeholder={placeholder}
        onPress={handleOpenPicker}
        onDelete={handleDelete}
      />

      <DatePickerModal
        locale={language}
        mode="range"
        visible={isPickerVisible}
        onDismiss={handleDismiss}
        startLabel={t('filters.dateRange.start')}
        endLabel={t('filters.dateRange.end')}
        startDate={startDate ?? undefined}
        endDate={endDate ?? undefined}
        onChange={handleChange}
        onConfirm={handleConfirm}
      />
    </>
  );
};
